import { logger } from "@/utils/logger";
import { MessagePart } from './types';
import { 
  processIntro, 
  processShowroom, 
  processClosing, 
  processSimilarVehicles, 
  processRootLevelProposedLink,
  processWindowSticker
} from './replyMessageProcessors';
import { 
  processReplyOptions, 
  processInventoryOrVehicleCount, 
  processOpeningHoursOrServiceForm,
  shouldSkipReplyOptions,
  processPromotions 
} from './replyMessageHelpers';
import { processCarfaxUrl } from './carfaxReplyProcessor';

export interface ParseReplyMessageOptions {
  skipIntroAndClosing?: boolean; // Skip intro/closing when they were already streamed
}

export const parseReplyMessage = async (jsonData: any, options?: ParseReplyMessageOptions): Promise<MessagePart[]> => {
  const parts: MessagePart[] = [];
  const replyMessage = jsonData.Reply_Message;
  const skipIntroAndClosing = options?.skipIntroAndClosing ?? false;
  
  if (!replyMessage || typeof replyMessage !== 'object') {
    return parts;
  }
  
  if (skipIntroAndClosing) {
    logger.log('🌊 [PARSER] Skipping intro and closing - already streamed to user');
  }
  
  // Normalize Reply_Message_Content to Reply_Message.Content
  // This handles cases where AI returns Reply_Message_Content at root level
  if (jsonData.Reply_Message_Content && !replyMessage.Content) {
    logger.log('📋 Normalizing Reply_Message_Content to Reply_Message.Content');
    replyMessage.Content = jsonData.Reply_Message_Content;
  }
  
  // Process Content.Actions for EV Boost integration
  const actions = replyMessage.Content?.Actions;
  if (actions && (actions.ev_compare_model || actions.ev_compare_year || actions.ev_compare_trim)) {
    const model = actions.ev_compare_model;
    const year = actions.ev_compare_year;
    const trim = actions.ev_compare_trim;
    logger.log('⚡ EV Boost Action detected:', { model, year, trim });
    
    // Execute EVBoostAPI.setVehicle if available
    if (typeof window !== 'undefined' && (window as any).EVBoostAPI?.setVehicle) {
      try {
        const vehicleData: { type: string; year?: string; model?: string; trim?: string } = {
          type: 'ice'
        };
        if (year) vehicleData.year = year;
        if (model) vehicleData.model = model;
        if (trim) vehicleData.trim = trim;
        
        (window as any).EVBoostAPI.setVehicle(vehicleData);
        logger.log('✅ EVBoostAPI.setVehicle executed successfully');
      } catch (e) {
        logger.warn('⚠️ Failed to execute EVBoostAPI.setVehicle:', e);
      }
    } else {
      logger.log('⚠️ EVBoostAPI.setVehicle not available on this page');
    }
  }
  
  // Check if any similar vehicles/proposed link URLs contain "search.html?"
  const initialSimilarVehiclesUrl = replyMessage.Similar_Vehicles || replyMessage.Proposed_Link || replyMessage.Content?.Proposed_Link || jsonData.Proposed_Link;
  const isSearchUrl = initialSimilarVehiclesUrl && initialSimilarVehiclesUrl.includes('search.html?');
  
  // Process intro text (strip links if search URL) - now returns array
  // Skip if intro was already streamed to user
  if (!skipIntroAndClosing) {
    const introParts = await processIntro(replyMessage.Intro, isSearchUrl);
    if (introParts.length > 0) parts.push(...introParts);
  }
  
  // Process CARFAX button right after intro (if URL exists)
  const carfaxUrl = replyMessage.Content?.CARFAX_URL || jsonData.CARFAX_URL;
  if (carfaxUrl) {
    logger.log('🚗 FOUND CARFAX_URL, adding button after intro:', carfaxUrl);
    parts.push({ 
      type: 'carfax_button', 
      content: { carfaxUrl } 
    });
  }
  
  // Process window sticker (after intro, before other content)
  const windowStickerPart = processWindowSticker(replyMessage.Content?.Window_Sticker);
  if (windowStickerPart) parts.push(windowStickerPart);
  
  // Process OEM Content images (after window sticker, before showroom buttons)
  if (replyMessage.Content?.Showroom?.OEM_Content) {
    const oemContent = replyMessage.Content.Showroom.OEM_Content;
    if (oemContent.image_url && Array.isArray(oemContent.image_url) && oemContent.image_url.length > 0) {
      logger.log('🖼️ FOUND OEM_Content with images:', oemContent.image_url.length);
      parts.push({ type: 'oem_content', content: oemContent });
    }
  }
  
  // Process showroom buttons
  let showroomPart = null;
  try {
    showroomPart = processShowroom(replyMessage.Content?.Showroom, replyMessage);
    if (showroomPart) {
      logger.log('🏢 Added showroom part with', Array.isArray(showroomPart.content?.suggested_models) ? showroomPart.content.suggested_models.length : 0, 'models');
      parts.push(showroomPart);
    }
  } catch (error) {
    console.error('❌ Error processing showroom:', error);
  }
  
  // Process inventory/vehicle count first (priority over reply options)
  let inventoryPart = null;
  try {
    inventoryPart = await processInventoryOrVehicleCount(replyMessage);
    logger.log('📦 Inventory processing result:', {
      hasInventoryPart: !!inventoryPart,
      partType: inventoryPart?.type,
      contentLength: Array.isArray(inventoryPart?.content) ? inventoryPart.content.length : 0
    });
    if (inventoryPart) {
      parts.push(inventoryPart);
    }
  } catch (error) {
    console.error('❌ Error processing inventory:', error);
  }
  
  // Process promotions (after inventory, before reply options)
  const promotionsPart = processPromotions(replyMessage);
  if (promotionsPart) {
    parts.push(promotionsPart);
  }
  
  // Check if CARFAX URL was already added (so we can skip reply options)
  const hasCarfaxUrl = !!(replyMessage.Content?.CARFAX_URL || jsonData.CARFAX_URL);
  
  // Process reply options if they exist and shouldn't be skipped
  if (!inventoryPart && !hasCarfaxUrl && !shouldSkipReplyOptions(replyMessage, jsonData)) {
    const replyOptionsPart = processReplyOptions(replyMessage, jsonData);
    if (replyOptionsPart) {
      logger.log('*** ✅ FOUND Reply_Options, adding to parts:', replyOptionsPart.content);
      parts.push(replyOptionsPart);
    } else {
      logger.log('*** ❌ Reply_Options not found or processed');
    }
  } else {
  }
  
  // Process opening hours or service form
  const openingHoursParts = processOpeningHoursOrServiceForm(replyMessage, jsonData);
  if (openingHoursParts && openingHoursParts.length > 0) {
    parts.push(...openingHoursParts);
  }
  
  // Process closing text - check both inside Reply_Message and at root level
  // Skip if closing was already streamed to user
  if (!skipIntroAndClosing) {
    const closingText = replyMessage.Closing || jsonData.Closing;
    const closingPart = processClosing(closingText);
    if (closingPart) parts.push(closingPart);
  }
  
  // Process similar vehicles - check both inside Reply_Message and at root level
  try {
    const inventory = replyMessage.Content?.Inventory ?? replyMessage.Inventory;
    const hasVehicles = Array.isArray(inventory) && inventory.length > 0;
    const similarVehiclesUrl = replyMessage.Similar_Vehicles || jsonData.Similar_Vehicles;
    const proposedLinkUrl = replyMessage.Proposed_Link || replyMessage.Content?.Proposed_Link || jsonData.Proposed_Link;
    const ctaText = replyMessage.Content?.Proposed_Link_CTA || jsonData.Proposed_Link_CTA || replyMessage.Proposed_Link_CTA;
    const similarVehiclesPart = processSimilarVehicles(similarVehiclesUrl, proposedLinkUrl, hasVehicles, ctaText);
    if (similarVehiclesPart) {
      parts.push(similarVehiclesPart);
    }
    
    // Process root-level proposed link (only if not already handled above)
    if (!similarVehiclesPart) {
      const rootProposedLinkPart = processRootLevelProposedLink(jsonData, replyMessage);
      if (rootProposedLinkPart) {
        parts.push(rootProposedLinkPart);
      }
    }
  } catch (error) {
    console.error('❌ Error processing proposed link:', error);
  }
  
  return parts;
};